import { ChangeEvent, useEffect, useState } from 'react'
import { Transition } from '@headlessui/react'
import Translation from 'components/Translation'
import {
    ClientCombobox
} from 'components/BookingPageComponents/ClientCombobox'
import { BookingDto, BookingForm } from 'types/booking'
import {
    ClientMembershipItem,
    MembershipClientOutput,
} from 'types/client-membership'
import {
    getApiRequest,
    postApiRequest,
    putApiRequest,
    useFetch,
    useApiRequest,
} from 'utils/fetch-helper'
import { validateEmail } from 'utils/string-helper'
import { FetchMethods } from 'utils/types'
import { MembershipSelect } from 'components/BookingPageComponents/MembershipComponent'
import FreeAppointmentToggle from 'components/BookingPageComponents/FreeAppointmentToggle'
import CheckoutForm from 'components/BookingPageComponents/CheckoutForm'
import { Stripe, StripeCardElement } from '@stripe/stripe-js'
import {
    toCurrency,
    toHHMM12,
    toYmd,
} from 'utils/string-localization-helper'
import AlertError from './AlertError'
import { DiscountType } from 'types/discount'
import { useTenantContext } from 'context/TenantContext'
import RecurringBookings, { generateRecurringBookingConfirmPaymentDTO, generateRecurringOrderBookingsDTO } from 'components/RecurringBookings'
import { CalendarSlotItem } from 'types/calendar'
import { BookingConfirmPaymentWithoutBookingDTO, BookingDTO, OrderBookingWithoutBookingDTO, OrderDTO, RecurringBookingDates } from 'types/order-booking'
import { ServiceApiTypeEnum, ServiceItem } from 'types/service'
import { OrderType } from 'types/order'
import { PaymentMethod } from 'types/payment'
import { Client } from 'types/client'
import { useAppContext } from 'context/AppContext'
import { classNames } from 'utils/dom-helpers'

export interface AddAttendeePropsUIProps {
    show: boolean
    booking_data: BookingForm
    title: string
    onToggleShow(show: boolean): void
    closeAndOpenNewClient(): void
    onSuccess(
        booking: Record<string, string | Record<string, string>>
    ): void
    isAppointment?: boolean
    appointmentData?: CalendarSlotItem | undefined
}
export default function AddAttendee({
    booking_data,
    show,
    title,
    onToggleShow,
    onSuccess,
    closeAndOpenNewClient,
    isAppointment = false,
    appointmentData = undefined,
}: AddAttendeePropsUIProps) {
    const { getApiRequest } = useApiRequest()
    const { is_mobile } = useAppContext()
    const { tenant, services } = useTenantContext()
    const [stripe, setStripe] = useState<Stripe>()
    const [card, setCard] = useState<StripeCardElement>()
    const [error_message, setErrorMessage] = useState<string>('')
    const [service, setService] = useState<ServiceItem | undefined>()
    const [is_recurring, toggleRecurring] = useState<boolean>(false)
    const [recurring_dates, setRecurringDates] = useState<RecurringBookingDates[]>([])
    const [valid_recurring_count, setValidRecurringCount] = useState<number>(0)
    const [is_editing, toggleEditing] = useState<boolean>(false)
    const [client, setClient] = useState<Client>()
    const [membership_options, setMembershipOptions] = useState<
        ClientMembershipItem[]
    >([])
    const [selected_membership, selectMembership] =
        useState<ClientMembershipItem>()
    const [is_free, setAsFree] = useState<boolean>(false)

    const [form, setFormData] = useState<
        BookingForm & {
            availability_id?: string
            appointment_id?: string
            start?: Date
            end?: Date
        }
    >(booking_data)
    const [check_box, setCheckBox] = useState<boolean>(false)

    const [is_submitting, toggleSubmit] = useState<boolean>(false)

    const { is_loading, data } = useFetch(
        `/v1/memberships/clients/by-service?clientEmail=${encodeURIComponent(
            client?.user.email as string
        )}&serviceId=${form.service_id}&tenantId=${tenant?.id}${service?.service_type == ServiceApiTypeEnum.SERIES ? `&groupClassAvailabilityId=${form.availability_id}` : ""}`,
        FetchMethods.GET,
        !!client && !!form.service_id && validateEmail(client.user.email)
    )

    async function createPaymentMethod(): Promise<string | void> {
        if (!stripe || !booking_data.connected_account_id) {
            setErrorMessage('no_payment_processor_integration')
            return
        }
        if (!card) return
        const result = await stripe.createPaymentMethod({
            type: 'card',
            card,
        })
        if (result.error) {
            setErrorMessage(result.error.code || 'card_error')
            return
        }
        return result.paymentMethod.id
    }

    function handleSubmit() { }

    async function submitForm() {
        toggleSubmit(true)
        if (!isAppointment) {
            if (!tenant?.id || !form.client_id) return
            const tenantId = tenant.id
            const {
                client_id: clientId,
                location_id: locationId,
                service_id: serviceId,
                amount,
                coupon_code: couponCode,
                discount,
                discount_type,
                start,
                end,
                order_type: type,
                appointment_id,
                availability_id
            } = form

            if (is_recurring && recurring_dates.length > 0) {
                const duration = services.find((service) => service.id == serviceId)?.duration
                const has_discount = (is_free || (discount_type == DiscountType.PERCENT && discount == 100) || form.amount == 0)
                let res
                if (selected_membership || has_discount) {
                    res = await postApiRequest('/v1/orders/order-booking-from-list', generateRecurringOrderBookingsDTO({
                        amount: selected_membership || has_discount ? 0 : (amount || 0) * (valid_recurring_count),
                        order: {
                            clientId,
                            tenantId,
                            type: OrderType.SERVICE,
                            couponCode: couponCode,
                            fullyPaid: (selected_membership || has_discount ? false : true),
                            membershipClientId: selected_membership?.id || undefined,
                            adhocDiscount: has_discount ? 100 : undefined,
                            adhocDiscountType: has_discount ? DiscountType.PERCENT : undefined
                        } as OrderDTO
                    } as OrderBookingWithoutBookingDTO,
                        {
                            groupClassAvailabilityId: availability_id,
                            clientId,
                            tenantId,
                        } as BookingDTO, recurring_dates || [], duration || 0))
                } else {
                    let paymentMethodId = await createPaymentMethod()
                    res = await postApiRequest('/v1/payments/confirm-booking-payment-from-list', generateRecurringBookingConfirmPaymentDTO({
                        amount: (amount || 0),
                        order: {
                            clientId,
                            tenantId,
                            type: OrderType.SERVICE,
                            couponCode: couponCode,
                            fullyPaid: true,
                            paymentMethod: PaymentMethod.CARD,
                            paymentMethodId
                        } as OrderDTO,

                        tenantId: tenant?.id,
                        savePaymentMethod: false,
                        paymentMethodId
                    } as BookingConfirmPaymentWithoutBookingDTO,
                        {
                            groupClassAvailabilityId: availability_id,
                            clientId,
                            tenantId,
                        } as BookingDTO, recurring_dates || [], duration || 0))
                }

                if (res?.ok) {
                    onSuccess({
                        ...res, client
                    })
                    resetModal()
                } else {
                    setErrorMessage(res?.message || "common.request_timed_out")
                }
            } else {
                let url = '/v1/payments/confirm-booking-payment'
                let paymentMethodId, booking
                let dto: BookingDto = {
                    amount,
                    clientId,
                    tenantId,
                    locationId,
                    savePaymentMethod: false,
                    order: {
                        clientId,
                        tenantId,
                        couponCode,
                        type: form.order_type,
                    },
                    booking: {
                        clientId,
                        tenantId,
                    },
                }
                if (
                    form.appointment_id &&
                    serviceId &&
                    locationId &&
                    form.start &&
                    form.end
                ) {
                    dto.booking.appointmentDto = {
                        serviceId,
                        locationId,
                        date: toYmd(form.start),
                        startTime: toHHMM12(form.start),
                        endTime: toHHMM12(form.end),
                    }
                }
                if (form.availability_id) {
                    dto.booking.groupClassAvailabilityId = form.availability_id
                }

                if (selected_membership) {
                    url = '/v1/orders/order-booking'
                    dto.order.membershipClientId = selected_membership.id
                    dto.order.fullyPaid = false
                    dto.amount = 0
                } else if (
                    is_free ||
                    (discount_type == DiscountType.PERCENT &&
                        discount == 100) ||
                    form.amount == 0
                ) {
                    url = '/v1/orders/order-booking'
                    dto.order.adhocDiscount = 100
                    dto.order.adhocDiscountType = DiscountType.PERCENT
                    dto.order.fullyPaid = false
                    dto.amount = 0
                } else {
                    paymentMethodId = await createPaymentMethod()
                    if (paymentMethodId) dto.paymentMethodId = paymentMethodId
                    dto.order.paymentMethod = 'CARD'
                }

                if (Object.keys(dto).length) {
                    booking = await postApiRequest(url, dto)
                    if (booking.message) {
                        setErrorMessage(booking.message)
                        setCheckBox(false)
                    } else {
                        setCheckBox(false)
                        onSuccess({
                            ...booking,
                            client,
                        })
                        resetModal()
                    }
                }
            }
        } else {
            const {
                appointment_id,
                location_space_id,
                service_id,
                staff_availability_id,
                start,
                attendees,
                location_id,
                staff_id,
                startTime,
                end,
            } = appointmentData as CalendarSlotItem

            const current_appointment = await getApiRequest(
                `/v1/appointments/${appointment_id}`
            )

            const start_hms = toHHMM12(startTime || start)

            const payload = {
                attendees: [...current_appointment?.attendees, client?.id],
                date: toYmd(start),
                id: appointment_id,
                locationId: location_id,
                locationSpaceId: location_space_id,
                serviceId: service_id,
                staffAvailabilityId: staff_availability_id,
                staffId: staff_id,
                startTime: start_hms,
                endTime: toHHMM12(end),
            }

            const url = `/v1/appointments/${appointment_id}`
            const res = await putApiRequest(url, payload)
            if (res?.ok) {
                onSuccess({
                    appointmentData: { ...res, clientData: client as any },
                })
            } else {
                setErrorMessage(res?.message || "common.request_timed_out")
            }
        }

        toggleSubmit(false)
    }

    function closeModal() {
        resetModal()
        onToggleShow(false)
    }

    function resetModal() {
        setCard(undefined)
        setClient(undefined)
        setMembershipOptions([])
        setAsFree(false)
        setRecurringDates([])
        setValidRecurringCount(0)
        toggleRecurring(false)
        toggleEditing(false)
    }

    useEffect(() => {
        if (form.service_id) {
            setService(services.find((service) => service.id == form.service_id))
        }
    }, [form.service_id])

    useEffect(() => {
        if (!is_loading && !!data && !!data.length) {
            if (!data || data.length === 0) setMembershipOptions([])
            else {
                const memberships = data.map(
                    (mc: MembershipClientOutput) => {
                        const { membership, membershipClient } = mc
                        const client_membership: ClientMembershipItem = {
                            id: membershipClient.id,
                            membership: {
                                id: membership.id as string,
                                name: membership.name,
                                img: membership?.imageUrl as string,
                            },
                            client: {
                                id: membershipClient.clientId,
                                used: {
                                    services:
                                        membershipClient.usedServiceCount,
                                    minutes: membershipClient.usedMinutes,
                                    amount: membershipClient.usedAmount,
                                },
                                starts_at: membershipClient.startsAt,
                                expires_at: membershipClient.expiresAt,
                                renews_at: membershipClient.renewsAt,
                            },
                            service_count: mc.availableServiceCount,
                            minute_count: mc.availableMinutes,
                            amount: mc.availableAmount,
                            unlimited: mc.redemptionUnlimited,
                            entitled: mc.entitled,
                            not_entitled_reason: mc.notEntitledReason,
                            content_tag: mc.contentTag,
                            member_price: mc.memberPrice,
                            member_price_type: mc.memberPriceType,
                            service_price: mc.servicePrice,
                            service_full_price: mc.serviceFullPrice,
                            discount_amount: mc.discountAmount,
                        }
                        return client_membership
                    }
                )
                setMembershipOptions(memberships)
            }
        }
    }, [data, is_loading])

    const closeModalOnClick = () => {
        closeModal()
        setCheckBox(false)
    }

    useEffect(() => {
        if (is_recurring
            && !is_free
            && form.connected_account_id
            && client !== undefined
            && selected_membership === undefined) {
            toggleRecurring(false)
        }
    }, [is_free, form?.connected_account_id, client, selected_membership])

    return (
        <Transition
            appear
            show={show}
            enter="transition-all duration-300"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="transition-all duration-300"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-full"
            className={classNames("flex flex-col h-screen add-attendee-popup", is_mobile ? "w-screen relative" : "justify-center")}
        >
            <div className={classNames("px-8 pb-8 bg-white rounded-lg overflow-x-hidden", is_mobile ? "absolute w-full" : "max-w-[60%]  mx-auto")}>
                <div className="flex justify-end pt-2">
                    <button
                        type="button"
                        onClick={() => closeModalOnClick()}
                        disabled={is_submitting}
                    >
                        <i className="feather-x text-xl" />
                    </button>
                </div>
                <div className="mt-2 mb-10">
                    <h3
                        className={`${error_message ? 'text-red-700' : 'text-black'
                            } leading-none whitespace-pre-line mx-auto attende-title`}
                    >
                        <Translation
                            content_key={title}
                            render_as="span"
                        />
                        <small className="block">
                            Search from list of clients or add a new client
                        </small>
                    </h3>
                </div>

                <div className="flex flex-col gap-6">
                    {tenant?.id ? (
                        <div className="flex  gap-2 relative z-20 items-center">
                            <div className='flex-1'>
                            <ClientCombobox
                                tenant_id={tenant.id}
                                onSelect={(
                                    selected_client: Client
                                ) => {
                                    setClient(selected_client)
                                    if (!selected_client) {
                                        setMembershipOptions([])
                                    } else {
                                        if (!isAppointment) {
                                            form.client_id =
                                                selected_client.id
                                            getApiRequest(
                                                `/v1/payments/client-payment-method?clientId=${selected_client.id}&tenantId=${tenant.id}`
                                            ).then()
                                        }
                                    }
                                }}
                            />
                                </div>
                            {!isAppointment && (
                                <button
                                    type="button"
                                    className="aot-btn gap-2 w-fit inline-flex items-center justify-center px-4 py-2 text-sm font-medium border-primary/20 text-primary border rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary-light"
                                    onClick={() => {
                                        resetModal()
                                        closeAndOpenNewClient()
                                    }}
                                >
                                    <i className="feather-plus" />
                                    <Translation content_key="new_client" />
                                </button>
                            )}
                        </div>
                    ) : (
                        ''
                    )}
                    {!isAppointment && (
                        <div
                            className={
                                is_free || membership_options.length === 0
                                    ? 'filter grayscale opacity-40 relative'
                                    : ''
                            }
                        >
                            {is_free ? (
                                <div className="w-full h-full absolute top-0 left-0 bg-transparent" />
                            ) : (
                                <>
                                    <MembershipSelect
                                        options={membership_options}
                                        onSelect={(
                                            membership: ClientMembershipItem
                                        ) => {
                                            selectMembership(membership)
                                        }}
                                    />
                                    {membership_options.length === 0 ? (
                                        <div className="h-9" />
                                    ) : (
                                        ''
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="relative z-0 pb-6 sm:pb-0">
                        <FreeAppointmentToggle
                            class_names="free-appointment"
                            value={isAppointment}
                            onChange={(val: boolean) => {
                                setAsFree(val)

                                if (val) {
                                    selectMembership(undefined)
                                    setCheckBox(false)
                                }
                            }}
                        />
                    </div>

                    {form?.start && form?.end && form?.service_id && service?.service_type == ServiceApiTypeEnum.GROUP
                        &&
                        <div>
                            <RecurringBookings
                                primary_color='#4b95b7'
                                start={form.start}
                                end={form.end}
                                is_recurring={is_recurring}
                                toggleRecurring={toggleRecurring}
                                recurring_dates={recurring_dates}
                                setRecurringDates={setRecurringDates}
                                service_id={form.service_id}
                                service_type={ServiceApiTypeEnum.GROUP}
                                location_id={form.location_id || ""}
                                is_editing={is_editing}
                                toggleEditing={toggleEditing}
                                grid_count={1}
                                valid_recurring_count={valid_recurring_count}
                                setValidRecurringCount={setValidRecurringCount}
                                membership_client={selected_membership}
                            />
                        </div>
                    }

                    {!is_free &&
                        form.connected_account_id &&
                        client !== undefined &&
                        selected_membership === undefined &&
                        !isAppointment && (
                            <fieldset>
                                <Translation
                                    content_key="payment"
                                    render_as="h4"
                                    className="text-black"
                                />

                                <CheckoutForm
                                    booking_data={{
                                        ...form,
                                        email: client.user?.email,
                                    }}
                                    className="mt-1"
                                    setBookingData={setFormData}
                                    handleSubmit={handleSubmit}
                                    setCard={(card: StripeCardElement) => {
                                        setCard(card)
                                    }}
                                    setStripe={setStripe}
                                />
                                <div className="flex items-center px-0 mb-12">
                                    <input
                                        type="checkbox"
                                        checked={check_box}
                                        className="h-5 w-5  border-gray-300 rounded text-primary focus:ring-primary-light checked:bg-primary-light"
                                        onChange={() => setCheckBox(!check_box)}
                                    />
                                    <span className="ml-2 text-sm">
                                        {' '}
                                        <Translation content_key="add_attendee_description" />
                                    </span>
                                </div>
                                <p className="rounded-lg bg-primary-lighter/50 px-3 py-2">
                                    <Translation
                                        content_key="customer_will_be_billed"
                                        content_values={{
                                            name: [
                                                client.user.first_name,
                                                client.user.last_name,
                                            ].join(' '),
                                        }}
                                        render_as="span"
                                    />{' '}
                                    {toCurrency(is_recurring ? (form.amount || 0) * valid_recurring_count : form.amount || 0)}.
                                </p>
                            </fieldset>
                        )}

                    <div className="flex items-center justify-end gap-4 brdr-top">
                        <button
                            type="button"
                            className="disabled:border-gray-100 disabled:text-gray-200 border border-gray-200 rounded-lg  text-sm aot-btn"
                            onClick={() => {
                                closeModal()
                                setCheckBox(false)
                            }}
                            disabled={is_submitting}
                        >
                            <Translation content_key="cancel" />
                        </button>

                        <button
                            type="button"
                            className="disabled:bg-gray-300 bg-primary text-white rounded-lg text-sm aot-btn"
                            onClick={(e) => {
                                e.preventDefault()
                                submitForm()
                            }}
                            disabled={
                                (is_recurring && (is_editing || valid_recurring_count == 0)) ||
                                (isAppointment
                                    ? !!client
                                        ? false
                                        : true
                                    : is_submitting ||
                                    (!is_free &&
                                        selected_membership ===
                                        undefined &&
                                        !!form.connected_account_id &&
                                        !!form.amount &&
                                        !check_box))
                            }
                        >
                            {' '}
                            {is_submitting ? (
                                <svg
                                    role="status"
                                    className="inline w-4 h-4 mr-2 mt-0 text-gray-200 animate-spin dark:text-gray-600"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                    />
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="#1C64F2"
                                    />
                                </svg>
                            ) : null}
                            <Translation content_key="add_and_book_attendee" />
                        </button>
                    </div>

                    <div className="fixed top-0 left-0 z-50">
                        <AlertError
                            title={error_message}
                            onClose={() => {
                                setErrorMessage('')
                            }}
                            class_name="w-80 lg:w-[30rem]"
                        />
                    </div>
                </div>
            </div>
        </Transition>
    )
}
